import { useEffect, useState } from 'react';
import { AlertTriangle, Clapperboard, Loader2 } from 'lucide-react';
import { isRendering } from './useMovieProject';
import { absUrl } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const RATIO = {
  '16:9': 'aspect-video w-full',
  '21:9': 'aspect-[21/9] w-full',
  '1:1': 'aspect-square w-full max-w-[320px]',
  '9:16': 'aspect-[9/16] w-full max-w-[240px]',
};

export function useElapsed(startIso, active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  if (!startIso) return '00:00';
  const s = Math.max(0, Math.floor((now - new Date(startIso).getTime()) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Shot preview with live states: empty → painting still → still → rendering motion → playable video. */
export function LiveMedia({ scene, aspect, busyStill, busyRender, testId }) {
  const rendering = busyRender || isRendering(scene) || scene.stage === 'rendering';
  const painting = (busyStill || scene.stage === 'still') && !scene.still_url;
  const elapsed = useElapsed(scene.video_rendered_at, rendering && !scene.video_url);
  const failed = scene.stage === 'failed' || scene.video_status === 'failed';
  const living = scene.motion_mode === 'still' && !scene.video_url && !rendering;

  return (
    <div data-testid={testId}
      className={cn('relative mx-auto overflow-hidden rounded-lg border border-neutral-800 bg-black', RATIO[aspect] || RATIO['16:9'])}>
      {scene.video_url ? (
        <video src={absUrl(scene.video_url)} poster={absUrl(scene.still_url) || undefined} controls playsInline preload="metadata"
          className="h-full w-full object-cover" data-testid={testId && `${testId}-video`} />
      ) : scene.still_url ? (
        <img src={absUrl(scene.still_url)} alt={scene.title || 'Storyboard still'}
          className={cn('h-full w-full object-cover transition duration-700', rendering && 'scale-105 brightness-[0.55]', living && 'living-still')} />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[radial-gradient(circle_at_30%_20%,#262626,#0a0a0a)] text-neutral-600">
          <Clapperboard size={22} />
          <span className="text-[11px] uppercase tracking-widest">Awaiting storyboard</span>
        </div>
      )}

      {painting && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-neutral-950/80" data-testid={testId && `${testId}-painting`}>
          <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-amber-500/10 via-transparent to-fuchsia-500/10" />
          <Loader2 size={20} className="animate-spin text-amber-300" />
          <span className="text-xs text-neutral-300">Painting storyboard frame…</span>
        </div>
      )}

      {rendering && !scene.video_url && (
        <div className="absolute inset-0 flex flex-col justify-between p-3" data-testid={testId && `${testId}-rendering`}>
          <div className="konnect-scan pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-transparent via-amber-300/15 to-transparent" />
          <div className="flex items-center gap-2 self-start rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-medium text-amber-200 backdrop-blur">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" /> REC · rendering motion
          </div>
          <div className="self-end rounded-md bg-black/70 px-2.5 py-1 text-right backdrop-blur">
            <div className="font-mono text-sm text-white">{elapsed}</div>
            <div className="text-[10px] text-neutral-400">{scene.video_provider || 'render farm'} · preview appears automatically</div>
          </div>
        </div>
      )}

      {living && scene.still_url && (
        <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-200 backdrop-blur"
          data-testid={testId && `${testId}-living-still`}>Living Still · free</div>
      )}

      {failed && !rendering && !scene.video_url && (
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-red-950/85 px-3 py-2 text-[11px] text-red-200">
          <AlertTriangle size={13} /> {scene.error || 'Render failed — try again'}
        </div>
      )}
    </div>
  );
}
